import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import PageMeta from "@/components/base/PageMeta";

interface ServiceCategory {
  id: string;
  title: string;
  description: string;
  icon: string;
}

const siteUrl = import.meta.env.VITE_PUBLIC_SITE_URL || "https://hackerslodge.com";

const postProjectSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  url: `${siteUrl}/post-project`,
  name: "Post a Hacking Project — Hackers Lodge",
  description: "Select a hacking service category and post your project. Hire specialized freelance hackers for social media, penetration testing, database recovery, and more.",
  isPartOf: {
    "@type": "WebSite",
    name: "Hackers Lodge",
    url: siteUrl,
  },
};

const serviceCategories: ServiceCategory[] = [
  {
    id: "Social Media Hack",
    title: "Social Media Hack",
    description: "Account recovery, takedowns, monitoring, and access to social media platforms.",
    icon: "fas fa-share-nodes",
  },
  {
    id: "Blackhat Hacking",
    title: "Blackhat Hacking",
    description: "Offensive security operations including system exploitation, malware development, and APT.",
    icon: "fas fa-skull",
  },
  {
    id: "Penetration Testing",
    title: "Penetration Testing",
    description: "Authorized simulated attacks on your systems to identify vulnerabilities before real attackers do.",
    icon: "fas fa-shield-halved",
  },
  {
    id: "Database",
    title: "Database Hacking",
    description: "Access, extraction, modification, and recovery of data from various database systems.",
    icon: "fas fa-database",
  },
  {
    id: "Spy",
    title: "Spy & Surveillance",
    description: "Intelligence gathering, monitoring solutions, digital surveillance, and counter-surveillance.",
    icon: "fas fa-eye",
  },
  {
    id: "Mobile Hacking",
    title: "Mobile Hacking",
    description: "iOS and Android device access, app reverse engineering, and mobile forensics.",
    icon: "fas fa-mobile-screen",
  },
  {
    id: "Email Hacking",
    title: "Email Hacking",
    description: "Email account access, recovery, investigation, and security assessments.",
    icon: "fas fa-envelope",
  },
  {
    id: "Website Hacking",
    title: "Website Hacking",
    description: "Website access, defacement, content modification, and vulnerability exploitation.",
    icon: "fas fa-globe",
  },
  {
    id: "Cryptocurrency",
    title: "Cryptocurrency",
    description: "Crypto wallet recovery, blockchain forensics, and transaction tracing.",
    icon: "fab fa-bitcoin",
  },
  {
    id: "Other",
    title: "Other Services",
    description: "Custom hacking and security services not covered by the categories above.",
    icon: "fas fa-ellipsis",
  },
];

export default function ServiceSelectionPage() {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [showPaymentError, setShowPaymentError] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const paymentRef = useRef<HTMLElement>(null);

  const selectedCategory = serviceCategories.find((c) => c.id === selectedService);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && showPaymentError) {
        setShowPaymentError(false);
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [showPaymentError]);

  // Lock body scroll when popup is open
  useEffect(() => {
    if (showPaymentError) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [showPaymentError]);

  const handleContinue = () => {
    if (!selectedService) return;

    if (!paymentMethod) {
      setShowPaymentError(true);
      return;
    }

    // Store selections so the detail form can pre-fill them
    sessionStorage.setItem("selectedServiceCategory", selectedService);
    sessionStorage.setItem("selectedPaymentMethod", paymentMethod);

    navigate(`/post-project/${encodeURIComponent(selectedService)}`);
  };

  const handlePaymentChange = (value: string) => {
    setPaymentMethod(value);
  };

  const handleConfirmError = () => {
    setShowPaymentError(false);
    // Scroll to payment section
    setTimeout(() => {
      paymentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  };

  return (
    <div className="min-h-screen bg-[#161b22]">
      <PageMeta
        title="Post a Hacking Project — Hire Expert Hackers for Your Cybersecurity Needs"
        description="Select a hacking service category and post your project on Hackers Lodge. Hire specialized freelance hackers for social media hacking, penetration testing, database recovery, cryptocurrency tracing, mobile hacking, and more."
        keywords="post hacking project, hire hacker, cybersecurity project, penetration testing request, ethical hacker hire, security project posting"
        canonicalUrl={`${siteUrl}/post-project`}
        ogType="website"
        schema={postProjectSchema}
      />
      <Header />
      <main className="pt-20 md:pt-24">
        <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
          <Breadcrumb items={[{ label: "Home", to: "/" }, { label: "Post Project" }]} />
        </div>
        {/* Hero Section */}
        <section className="relative py-14 md:py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]" />
          <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle_at_50%_50%,_white_1px,_transparent_1px)] bg-[length:24px_24px]" />
          <div className="relative w-full px-4 md:px-8 max-w-5xl mx-auto text-center">
            <div className="w-16 h-16 md:w-20 md:h-20 bg-[#0d1117]/10 rounded-2xl flex items-center justify-center mx-auto mb-6 backdrop-blur-sm">
              <i className="fas fa-crosshairs text-[#5cb85c] text-2xl md:text-3xl" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-4 tracking-tight">
              What Service Do You Need?
            </h1>
            <p className="text-sm md:text-base text-gray-300 max-w-2xl mx-auto leading-relaxed">
              Select a category below to describe your project. Each service type is handled by
              specialized hackers with proven track records in that domain. All projects are confidential.
            </p>
          </div>
        </section>

        {/* Service Selection */}
        <section className="py-10 md:py-14">
          <div className="w-full px-4 md:px-8 max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-xs font-semibold tracking-widest uppercase text-[#5cb85c] mb-2 block">
                Choose Your Service
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-200">
                Select a Hacking Service Category
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                Pick from the dropdown below and click continue to start posting your project.
              </p>
            </div>

            <div className="bg-[#0d1117] rounded-xl border border-gray-700 p-6 md:p-8">
              {/* Service Dropdown */}
              <div className="mb-6" ref={dropdownRef}>
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Service Category <span className="text-red-400">*</span>
                </label>

                {/* Custom dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="w-full flex items-center justify-between px-4 py-3 border border-gray-600 rounded-lg text-sm text-left bg-[#0d1117] hover:border-[#5cb85c] transition-colors focus:outline-none cursor-pointer"
                  >
                    <span className={selectedCategory ? "text-gray-200" : "text-gray-400"}>
                      {selectedCategory ? (
                        <span className="flex items-center gap-2">
                          <i className={`${selectedCategory.icon} text-[#5cb85c]`} />
                          {selectedCategory.title}
                        </span>
                      ) : (
                        "Select a service category..."
                      )}
                    </span>
                    <i className={`fas fa-chevron-down text-gray-400 text-xs transition-transform duration-200 ${showDropdown ? "rotate-180" : ""}`} />
                  </button>

                  {showDropdown && (
                    <div className="absolute z-30 w-full mt-1 bg-[#0d1117] border border-gray-700 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                      {serviceCategories.map((cat) => (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => {
                            setSelectedService(cat.id);
                            setShowDropdown(false);
                          }}
                          className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-[#161b22] transition-colors ${
                            selectedService === cat.id ? "bg-green-900/20 text-[#5cb85c]" : "text-gray-300"
                          }`}
                        >
                          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                            selectedService === cat.id ? "bg-[#5cb85c]/10" : "bg-[#1c2128]"
                          }`}>
                            <i className={`${cat.icon} text-sm ${selectedService === cat.id ? "text-[#5cb85c]" : "text-gray-500"}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-xs">{cat.title}</div>
                            <div className="text-xs text-gray-400 truncate">{cat.description}</div>
                          </div>
                          {selectedService === cat.id && (
                            <i className="fas fa-check text-[#5cb85c] text-xs" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected service info */}
                {selectedCategory && (
                  <div className="mt-3 p-3 bg-green-900/20 border border-green-700/30 rounded-lg flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#5cb85c]/10 flex items-center justify-center flex-shrink-0">
                      <i className={`${selectedCategory.icon} text-[#5cb85c]`} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-200">{selectedCategory.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{selectedCategory.description}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Continue Button */}
              <button
                onClick={handleContinue}
                disabled={!selectedService}
                className="w-full bg-[#5cb85c] hover:bg-[#4cae4c] disabled:bg-gray-700 disabled:cursor-not-allowed text-white text-sm font-semibold py-3 rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-arrow-right mr-2" />
                {selectedService ? `Continue with ${selectedCategory?.title}` : "Select a Service to Continue"}
              </button>
            </div>
          </div>
        </section>

        {/* Payment Methods */}
        <section ref={paymentRef} className="py-10 md:py-14 bg-[#0d1117] border-t border-gray-800">
          <div className="w-full px-4 md:px-8 max-w-5xl mx-auto">
            <div className="text-center mb-8">
              <span className="text-xs font-semibold tracking-widest uppercase text-[#5cb85c] mb-2 block">
                Payment Options
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-200">
                Choose Your Payment Method
              </h2>
              <p className="text-sm text-gray-500 mt-2 max-w-xl mx-auto">
                Select how you&apos;d like to pay. We support cryptocurrency, escrow, and wire transfers.
              </p>
            </div>

            <div className="max-w-md mx-auto">
              <div className="bg-[#161b22] border border-gray-700 rounded-xl p-6">
                <label className="block text-sm font-semibold text-gray-300 mb-2">
                  Payment Method
                </label>
                <select
                  value={paymentMethod}
                  onChange={(e) => handlePaymentChange(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-600 rounded-lg text-sm text-gray-300 bg-[#0d1117] focus:outline-none focus:border-[#5cb85c] transition-colors cursor-pointer appearance-none"
                  style={{
                    backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3csvg%3e\")",
                    backgroundPosition: "right 0.75rem center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "1.25rem",
                    paddingRight: "2.5rem",
                  }}
                >
                  <option value="">-- Select a payment method --</option>
                  <option value="crypto">
                    Cryptocurrency (BTC, ETH, USDT, etc.)
                  </option>
                  <option value="escrow">
                    Escrow Service
                  </option>
                  <option value="wire">
                    Wire Transfer
                  </option>
                </select>

                <p className="text-xs text-gray-400 mt-3 leading-relaxed">
                  <i className="fas fa-info-circle mr-1" />
                  After selecting your payment method, click Continue above to fill in your project details.
                </p>
              </div>

              <div className="mt-4 p-4 bg-amber-900/20 border border-amber-700/30 rounded-xl">
                <p className="text-xs text-amber-400 leading-relaxed">
                  <i className="fas fa-exclamation-triangle mr-2" />
                  <strong>Important:</strong> Always verify the wallet address before sending funds.
                  Hackers Lodge is not responsible for funds sent to incorrect addresses. Transaction
                  confirmations may take a few minutes depending on network conditions.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-10 md:py-14 bg-gradient-to-b from-gray-900 to-[#1a1a2e]">
          <div className="w-full px-4 md:px-8 max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-sm text-gray-400 max-w-xl mx-auto mb-8">
              Choose a service category above, or if you&apos;re not sure what you need,
              select &quot;Other Services&quot; and describe your requirements — we&apos;ll match you with the right expert.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={() => navigate("/post-project/Other")}
                className="px-8 py-3 bg-[#5cb85c] hover:bg-[#4cae4c] text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-bolt mr-2" />Post Custom Project
              </button>
              <Link
                to="/projects-listing"
                className="px-8 py-3 border border-white/20 hover:border-white/40 text-white text-sm font-semibold rounded-lg transition-all whitespace-nowrap"
              >
                <i className="fas fa-list mr-2" />Browse Existing Projects
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Payment Method Error Popup */}
      {showPaymentError && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowPaymentError(false)}
          />

          {/* Popup */}
          <div className="relative z-10 bg-[#0d1117] border border-red-700/40 rounded-xl w-[90%] max-w-md p-6 mx-4 animate-in">
            {/* Icon */}
            <div className="w-14 h-14 rounded-full bg-red-900/30 border border-red-700/30 flex items-center justify-center mx-auto mb-4">
              <i className="fas fa-exclamation-triangle text-red-400 text-xl" />
            </div>

            {/* Message */}
            <h3 className="text-lg font-bold text-white text-center mb-2">
              Payment Method Required
            </h3>
            <p className="text-sm text-gray-300 text-center mb-6 leading-relaxed">
              Please, select a payment method below to continue.
            </p>

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowPaymentError(false)}
                className="flex-1 px-4 py-2.5 border border-gray-600 rounded-lg text-sm font-semibold text-gray-300 hover:bg-[#161b22] transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmError}
                className="flex-1 px-4 py-2.5 bg-[#5cb85c] hover:bg-[#4cae4c] rounded-lg text-sm font-semibold text-white transition-colors whitespace-nowrap"
              >
                <i className="fas fa-arrow-down mr-1.5" />
                Go to Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}